import React, {useEffect, useState} from "react"
import {useRoute} from "react-router5"
import {connect} from "react-redux"

import _ from "lodash"

import "pages/Styles/UIKit_v2.sass"
import "pages/Styles/ProfileV2.sass"

import {httpClient} from "funcs"

import Icon from "components/Icon"
import Popup from "../../components/Popup"
import InfoButton from "../../components/InfoButton"

import UserFields from "./UserFields"
import BankDetailsView from "./BankDetailsView"
import notifyUser, {notifyAboutError} from "../../methods/notifyUser"
import TGBotIntegrationView from "./TGBotIntegrationView"
import {supportLink, maxSupportLink} from "../../methods/openInNewTab"
import PromoActivatedPopup from "./PromoActivatedPopup"
import ActivatePromoCodePopup from "./ActivatePromoCodePopup"
import BecomePartnerInfoPopup from "./BecomePartnerInfoPopup"
import {checkPermission, UserPermission} from "../../methods/permissionByRole"
import {PageLoader} from "../Contracts/ContractList/PageLoader"
import {getToken, isOAuth2User} from "../../methods/saveToken"
import {loadSelf, signOut} from "../../methods/loadSelf"
import {
  formatActivatedPromoForProfile,
  getFirstPurchasePromoCode,
  hasUserCompletedPurchase,
} from "../../util/promoCode"
import MyPromoCodeView from "./MyPromoCodeView"
import IntegrationsView from "./IntegrationsPopup/IntegrationsView"
import AccountHeader from "./AccountHeader"
import TabSelectorV2 from "../Tariffs/TabSelectorV2"
import TeamView from "./TeamView"
import SessionsPopup from "./SessionsPopup"
import ProfileInitiatorTrustSection from "./ProfileInitiatorTrustSection"
import ProfileContractBrandingSection from "./ProfileContractBrandingSection"
import {ReactComponent as MaxIcon} from "images/pages/iconsV2/max.svg"
import SelectOptionsView from "../Contracts/SelectOptionsView"
import { ProfileTabs, profileTabFromQuery, profileTabToQuery } from "./profileTabQuery"

export { ProfileTabs, profileTabFromQuery, profileTabToQuery }

let ProfilePage: any = function ProfilePage(props: any) {
  const $router = useRoute()
  const [promoPopupShown, setPromoPopupShown] = useState(false)
  const [promoCode, setPromoCode] = useState<any>('')
  const [promoCodeActivated, setPromoCodeActivated] = useState<any>(false)
  const [codeActivationResult, setCodeActivationResult] = useState<any>(null)
  const [partnerRequestSent, setPartnerRequestSent] = useState(
    () => !!localStorage.getItem('partner_program_request_sent')
  )
  const [becomePartnerInfoPopupOpen, setBecomePartnerInfoPopupOpen] = useState(false)
  const [sessionsPopupOpen, setSessionsPopupOpen] = useState(false)
  const [logoutAllConfirmOpen, setLogoutAllConfirmOpen] = useState(false)
  const [jurisdictionInfoPopupOpen, setJurisdictionInfoPopupOpen] = useState(false)
  const [jurisdictionSaving, setJurisdictionSaving] = useState(false)
  const [openProfileEdit, setOpenProfileEdit] = useState(false)

  const tabFromUrl = profileTabFromQuery($router.route.params.tab)
  const savedTab = localStorage.getItem('selectedProfileTab') as ProfileTabs
  const selectedTab = tabFromUrl
    || props.$store.selectedProfileTab
    || (savedTab && Object.values(ProfileTabs).includes(savedTab) ? savedTab : null)
    || ProfileTabs.About

  // URL ?tab= имеет приоритет; иначе берём store / localStorage
  useEffect(() => {
    const fromUrl = profileTabFromQuery($router.route.params.tab)
    if (fromUrl) {
      if (fromUrl !== props.$store.selectedProfileTab) {
        props.$commitToStore({ selectedProfileTab: fromUrl })
        localStorage.setItem('selectedProfileTab', fromUrl)
      }
      return
    }
    if (!props.$store.selectedProfileTab) {
      const storedTab = localStorage.getItem('selectedProfileTab') as ProfileTabs
      const tabToSet = storedTab && Object.values(ProfileTabs).includes(storedTab)
        ? storedTab
        : ProfileTabs.About
      props.$commitToStore({
        selectedProfileTab: tabToSet
      })
    }
  }, [$router.route.params.tab])

  // Load user data
  useEffect(() => {
    if (!hasSession()) {
      return
    }

    loadSelf(props)
  }, [])

  function hasSession() {
    return getToken() != null
  }

  function userLoaded() {
    return props.$store.userSession != null
  }

  const jurisdictionOptions = [
    { id: 'RU', title: 'Россия' },
    { id: 'KZ', title: 'Казахстан' },
    { id: 'BY', title: 'Беларусь' },
    { id: 'AM', title: 'Армения' },
  ]

  function selectedJurisdictionOption() {
    const code = (props.$store.userSession?.jurisdiction || 'RU').toString().toUpperCase()
    return jurisdictionOptions.find(o => o.id === code) || jurisdictionOptions[0]
  }

  async function updateJurisdiction(option: any) {
    const nextCode = (option?.id || '').toString().toUpperCase()
    if (!nextCode) {
      return
    }
    const currentCode = (props.$store.userSession?.jurisdiction || 'RU').toString().toUpperCase()
    if (nextCode === currentCode) {
      return
    }

    try {
      setJurisdictionSaving(true)
      await httpClient.patch("/user/self", { jurisdiction: nextCode })
      props.$commitToStore({
        userSession: {
          ...props.$store.userSession,
          jurisdiction: nextCode
        }
      })
      notifyUser(
        props,
        "Юрисдикция обновлена",
        "Теперь в шаблоне/договоре будет использоваться соглашение о ПЭП, соответствующее выбранной юрисдикции."
      )
    } catch (err) {
      notifyAboutError(props, err)
    } finally {
      setJurisdictionSaving(false)
    }
  }

  async function activatePromoCode(code: string) {
    setPromoCode(code)
    setPromoPopupShown(false)

    httpClient.put("/user/activate_promo_code", { promo_code: code })
        .then(result => {
          const resultData = _.get(result, 'data', [])

          if (resultData) {
            setCodeActivationResult(resultData)
            setPromoCodeActivated(true)
            loadSelf(props)
          }
        })
        .catch(err => {
          notifyAboutError(props, err)
        })
  }

  function enterPromoCodeView() {
    if (!checkPermission(props, UserPermission.OWNER)) {
      return
    }
    
    const userSession = props.$store.userSession
    const isExistingClient = hasUserCompletedPurchase(userSession)
    const activePromo = getFirstPurchasePromoCode(userSession)
    const activatedPromo = formatActivatedPromoForProfile(activePromo)

    return (
      <div className="profile-section-card ui-kit-v2">
        <div className="profile-section-title">Активировать промо-код</div>
        <div className="profile-section-subtitle">
          {isExistingClient
            ? "Скидка на пакет действует только на первую покупку. Промокод на бесплатную настройку шаблона можно ввести в любой момент."
            : activatedPromo
              ? "Бонус по промо-коду будет применён при первой оплате пакета. Промокод на бесплатную настройку шаблона вводится отдельно."
              : "Введите промо-код от коллеги, чтобы получить бонусы: шаблоны документов, бонус к пакету или бесплатную настройку шаблона."}
        </div>

        {activatedPromo && (
          <>
            <div className="profile-activated-promo">
              <div className="profile-section-label">{activatedPromo.codeName}</div>
              {activatedPromo.bonusHint && (
                <span className="profile-activated-promo__bonus">({activatedPromo.bonusHint})</span>
              )}
            </div>
            <p className="profile-activated-promo__status">
              Промо-код «{activatedPromo.codeName}» уже активирован
            </p>
          </>
        )}

        <hr className="profile-section-divider" />
        <div className="profile-section-row">
          <button
            type="button"
            className="new ui-kit-v2-button action-button primary"
            onClick={() => setPromoPopupShown(true)}
          >
            <span className="button-text">Ввести промо-код</span>
          </button>
        </div>
      </div>
    )
  }

  function myPromoCodeView() {
    if (!props.$store.userSession.promo_code) {
      return null
    }
    return <div className="profile-section-card ui-kit-v2" style={{marginTop: "15px"}}>
      <MyPromoCodeView/>
    </div>
  }

  function supportView() {
    return <div className="profile-section-card ui-kit-v2">
      <div className="profile-section-title">
        Поддержка
      </div>

      <div className="profile-section-row">
        <a href={supportLink()} className="profile-support-link">
          <div className="profile-support-icon">
            <Icon className="__telegram" icon="telegram-1"/>
          </div>
          <span className="__telegram Body_16_v2">@OkiDokiSupportBot</span>
        </a>
      </div>

      <div className="profile-section-row">
        <a href={maxSupportLink()} className="profile-support-link">
          <div className="profile-support-icon">
            <MaxIcon className="__max" style={{width: 24, height: 24}} />
          </div>
          <span className="__max Body_16_v2">+7 (927) 200 3709</span>
        </a>
      </div>
    </div>
  }

  function signOutView() {
    return <div className="profile-section-card ui-kit-v2">
      <div className="profile-section-title">
        Выход
      </div>

      <div className="profile-section-row" style={{marginTop: "12px"}}>
        <button
            className="new action-button secondary"
            onClick={() => signOut(props)}
        >
          <span className="button-text">Выйти</span>
        </button>
      </div>
    </div>
  }

  function securityView() {
    const hasOAuth2 = isOAuth2User()
    return (
      <div className="profile-section-card ui-kit-v2">
        <div className="profile-section-title">
          Сессии и вход
        </div>
        <div className="profile-section-subtitle">
          {hasOAuth2
            ? "Просмотрите все устройства, на которых вы вошли в аккаунт, и при необходимости завершите сессию на любом из них."
            : "Вы можете выйти из аккаунта на всех устройствах. После этого потребуется повторный вход по номеру телефона."}
        </div>
        <hr className="profile-section-divider"/>
        <div className="profile-section-row" style={{marginTop: "12px"}}>
          {hasOAuth2 ? (
            <button
              className="new action-button primary"
              onClick={() => setSessionsPopupOpen(true)}
            >
              <span className="button-text">Управление сессиями</span>
            </button>
          ) : (
            <button
              className="new action-button secondary"
              onClick={() => setLogoutAllConfirmOpen(true)}
            >
              <span className="button-text">Завершить все сессии</span>
            </button>
          )}
        </div>
      </div>
    )
  }

  function notificationsView() {
    return <div id="tg-integration-section">
      <TGBotIntegrationView/>
    </div>
  }

  function handleTabChange(option: ProfileTabs) {
    props.$commitToStore({
      selectedProfileTab: option
    })
    localStorage.setItem('selectedProfileTab', option)
    const nextParams = {
      ...$router.route.params,
      tab: profileTabToQuery(option),
    }
    $router.router.navigate('profile', nextParams, { replace: true })
  }

  function getTabDisplayName(tabName: ProfileTabs): string {
    if (tabName === ProfileTabs.About) {
      // Определяем роль пользователя и возвращаем соответствующее название
      const userRole = props.$store.userSession?.role
      if (userRole === 'customer') {
        return 'Мои данные'
      } else {
        return 'О компании'
      }
    }
    return tabName
  }

  function renderTabContent() {
    switch (selectedTab) {
      case ProfileTabs.About:
        return (
          <>
            <div style={{marginBottom: "15px"}}>  
              <AccountHeader
                $store={props.$store}
                $commitToStore={props.$commitToStore}
                forceOpenProfileEdit={openProfileEdit}
                onForceOpenProfileEditConsumed={() => setOpenProfileEdit(false)}
              />
            </div>
            <UserFields />
            <BankDetailsView />
            <ProfileContractBrandingSection />
            <ProfileInitiatorTrustSection
              onRequestEditProfile={() => setOpenProfileEdit(true)}
            />
            <div className="profile-section-card ui-kit-v2" style={{marginTop: "15px"}}>
              <div
                className="profile-section-title"
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}
              >
                <span>Юрисдикция</span>
                <InfoButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setJurisdictionInfoPopupOpen(true)
                  }}
                />
              </div>
              <div className="profile-section-subtitle">
                Влияет на текст соглашения об электронном документообороте: оно само добавляется в каждый документ и не списывается из пакета.
              </div>
              <div className="profile-section-row" style={{ marginTop: "10px" }}>
                <SelectOptionsView
                  selectedOption={selectedJurisdictionOption()}
                  onSelectOption={(opt) => updateJurisdiction(opt)}
                  options={jurisdictionOptions}
                  placeholder="Выберите юрисдикцию"
                  freeSolo={false}
                  width="full"
                  disabled={jurisdictionSaving}
                  popperZIndex={2000}
                />
              </div>
            </div>
          </>
        )
      case ProfileTabs.Notifications:
        return notificationsView()
      case ProfileTabs.Integrations:
        return <IntegrationsView/>
      case ProfileTabs.Team:
        return <TeamView $store={props.$store} $commitToStore={props.$commitToStore}/>
      case ProfileTabs.Promo:
        return (
          <div className="profile-tab-content profile-tab-content--promo">
            {enterPromoCodeView()}
            {myPromoCodeView()}
            <div className="profile-section-card ui-kit-v2" style={{marginTop: "15px"}}>
              <div className="profile-section-title">
                Партнёрская программа
              </div>
              <div className="profile-section-subtitle">
                Станьте партнером по продвижению сервиса ОкиДоки и получайте вознаграждение за новых клиентов!
              </div>
              <hr className="profile-section-divider"/>
              <div className="profile-section-row" style={{marginTop: "10px"}}>
                {props.$store.userSession?.partner_info ? (
                  <button
                    className="new action-button primary"
                    onClick={() => $router.router.navigate('partner-program')}
                  >
                    <span className="button-text">Перейти</span>
                  </button>
                ) : (
                  <button
                    className="new action-button primary"
                    disabled={partnerRequestSent}
                    onClick={() => {
                      if (partnerRequestSent) return
                      setBecomePartnerInfoPopupOpen(true)
                    }}
                  >
                    <span className="button-text">
                      {partnerRequestSent ? 'Заявка отправлена' : 'Стать партнёром'}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      case ProfileTabs.Security:
        return <div className="profile-tab-content profile-tab-content--security">{securityView()}</div>
      default:
        return null
    }
  }

  // Render
  const tabDisplayNames = Object.values(ProfileTabs).map(t => getTabDisplayName(t))
  const selectedDisplayName = getTabDisplayName(selectedTab)

  return (
      <div>
        <div className="profile-v2-page ui-kit-v2-page-white-bg">
            <div className="divider-full-width" />
            <div className="mainContent">
                <div className="contentWrapper" style={{paddingTop: "10px"}}>
                    <div className="profile-content-stack">
                        <div className="profile-content-row">
                            <div className="profileSection">
                                <div className="profile-header-v2 ui-kit-v2">
                                    <div className="profile-header-top-row">
                                        <TabSelectorV2
                                            options={tabDisplayNames}
                                            selectedOption={selectedDisplayName}
                                            onSelectOption={(displayName) => {
                                                const tab = Object.values(ProfileTabs).find(t => getTabDisplayName(t) === displayName)
                                                if (tab) handleTabChange(tab)
                                            }}
                                        />
                                    </div>
                                </div>
                                <section>
                                    {
                                      !userLoaded() &&
                                      <PageLoader />
                                    }
                                    {
                                        userLoaded() &&
                                        <div>
                                            {renderTabContent()}
                                        </div>
                                    }
                                </section>
                            </div>
                            <aside className="profileAside">
                                {supportView()}
                                {signOutView()}
                            </aside>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {
            promoCodeActivated &&
            <PromoActivatedPopup
                result={codeActivationResult}
                promoCode={promoCode}
                onClose={() => {
                    setPromoCodeActivated(false)
                }}
            />
        }

        {promoPopupShown && (
            <ActivatePromoCodePopup
                onClose={() => setPromoPopupShown(false)}
                onActivate={activatePromoCode}
            />
        )}

        {becomePartnerInfoPopupOpen && (
          <BecomePartnerInfoPopup
            onClose={() => setBecomePartnerInfoPopupOpen(false)}
            onSuccess={() => {
              setPartnerRequestSent(true)
              localStorage.setItem('partner_program_request_sent', '1')
            }}
            onError={(err) => notifyAboutError(props, err)}
          />
        )}

        {sessionsPopupOpen && (
          <SessionsPopup
            onClose={() => setSessionsPopupOpen(false)}
            props={props}
          />
        )}

        {logoutAllConfirmOpen && (
          <Popup onClose={() => setLogoutAllConfirmOpen(false)}>
            <div className="logout-all-confirm-popup-v2 ui-kit-v2">
              <h2 className="Subheader_18 logout-all-confirm-popup-v2__title">
                Завершить все сессии?
              </h2>
              <p className="Body_16_v2_secondary logout-all-confirm-popup-v2__description">
                Вы выйдете из аккаунта на всех устройствах. Для повторного входа потребуется авторизация по номеру телефона.
              </p>
              <div className="buttons-container-v2">
                <button
                  type="button"
                  className="new ui-kit-v2-button action-button primary"
                  onClick={() => {
                    setLogoutAllConfirmOpen(false)
                    signOut(props)
                  }}
                >
                  <span className="button-text">Да, выйти везде</span>
                </button>
                <button
                  type="button"
                  className="new ui-kit-v2-button action-button secondary"
                  onClick={() => setLogoutAllConfirmOpen(false)}
                >
                  <span className="button-text">Отмена</span>
                </button>
              </div>
            </div>
          </Popup>
        )}

        {jurisdictionInfoPopupOpen && (
          <Popup onClose={() => setJurisdictionInfoPopupOpen(false)}>
            <div className="ui-kit-v2">
              <div className="popup-title">Что меняет юрисдикция?</div>
              <div className="Body_16_v2" style={{ marginTop: "10px" }}>
                Юрисдикция определяет, какой текст соглашения о простой электронной подписи (ПЭП) будет автоматически
                добавляться к договорам и шаблонам, которые вы создаёте и отправляете на подписание.
              </div>
              <div className="Body_16_v2_secondary" style={{ marginTop: "10px" }}>
                Это соглашение уже входит в каждый документ. Отдельно высылать согласие на ЭДО не нужно, и из пакета документов оно не списывается.
              </div>
              <div className="Body_16_v2_secondary" style={{ marginTop: "10px" }}>
                Если вы меняете юрисдикцию, новые документы будут формироваться с соответствующим соглашением.
              </div>
            </div>
          </Popup>
        )}
      </div>
  )
}

ProfilePage = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
      $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(ProfilePage)

export default ProfilePage
